/**
 * How wide a fill-in-the-blank field should be.
 *
 * A blank is not a form field: the learner is writing one known word into a
 * sentence, so the space should look like it fits *that* word. Sizing it from the
 * longest acceptable answer — the answer, its accepted alternatives, the words on
 * offer beside it, and whatever the learner has typed — keeps the sentence
 * readable as prose, and never hides the text being written into it.
 *
 * **Where the width comes from.** It used to come from arithmetic: one average
 * glyph width (0.62 em, Inter's) multiplied by a character count. That is a guess
 * dressed as a measurement, and it is wrong in exactly the place this app cares
 * about — a themed book's own face. The bundled hands are nothing like Inter's
 * average: the same five letters are a third wider in a ruqaa hand than the
 * estimate allows, and a field sized for Inter cuts the answer off in a book
 * written in one.
 *
 * So the width is *measured*, by the platform that will print it, through
 * `useTextWidths` — a hidden probe in the real font (see
 * `#/components/capsules/play/useTextWidths`). What is left here is the rule
 * around those measurements:
 *
 *  - take the widest of everything the blank can hold, measured where a
 *    measurement exists;
 *  - and where one does not — the first frame of a blank, a string a probe has
 *    not reported yet — fall back to the old estimate *for that string only*, so
 *    the field is never narrower than the writing it is showing while it waits.
 *    That fallback is a stand-in for one frame, not the rule.
 *
 * The result is capped and floored: a blank never collapses below a writable
 * minimum, and one absurd answer cannot push the sentence off the page.
 */
import {type TextStyle} from 'react-native'

/** The font a blank is printed in — what its width has to be measured in. */
export type BlankFont = {
  fontFamily?: TextStyle['fontFamily']
  fontSize: number
  fontWeight?: TextStyle['fontWeight']
  fontStyle?: TextStyle['fontStyle']
  letterSpacing?: TextStyle['letterSpacing']
}

/**
 * The average glyph width the estimate assumes, as a multiple of the type size.
 *
 * Only ever reached for a string that has not been measured yet, which is why it
 * stays deliberately generous: a slightly wide blank for one frame is a
 * cosmetic annoyance, and a too-narrow one loses the answer.
 */
const ASSUMED_ADVANCE = 0.62

/** The narrowest a blank may be, before its own padding. */
const MIN_TEXT = 34

/** The widest a blank may be, however long its answer is. */
export const BLANK_MAX_WIDTH = 320

/** What a string would take, on the average-glyph assumption. */
export function estimatedTextWidth(text: string, font: BlankFont): number {
  return text.length * font.fontSize * ASSUMED_ADVANCE
}

export type BlankWidthOptions = {
  /** The font this field will print in — what its width must be measured in. */
  font: BlankFont
  /**
   * The field's own horizontal room, in px: its padding either side plus its
   * borders. Passed in rather than assumed, so widening a field's padding cannot
   * leave its width behind.
   */
  padding: number
  /** The measured widths, where the platform has reported them. */
  measured?: Map<string, number>
  min?: number
  max?: number
}

export function blankInputWidth(
  candidates: readonly (string | undefined | null)[],
  {font, padding, measured, min, max = BLANK_MAX_WIDTH}: BlankWidthOptions,
): number {
  const widest = candidates.reduce((widest, candidate) => {
    const text = (candidate ?? '').trim()
    if (!text) return widest
    return Math.max(
      widest,
      measured?.get(text) ?? estimatedTextWidth(text, font),
    )
  }, 0)
  const width = Math.max(widest, MIN_TEXT) + padding
  return Math.round(Math.min(Math.max(width, min ?? 0), max))
}
